import { createContext, useContext } from 'react';

import { PlayerShapes, Sites, ToolTypes } from '@labradorsports/constants';

import { usePlayEditor } from './playEditor.js';

/** Default grass-like fill when "Real Field" is enabled (football, lacrosse, etc.). */
export const REAL_FIELD_FILL_DEFAULT = '#75B36F';

export const FIELD_LOGO_OPACITY = 0.15;
export const FIELD_LOGO_OPACITY_REAL_FIELD = 0.3;

export interface PlayThemeContextValue {
    colorPrimary: string;
    colorSecondary: string;
    logoUrl: string;
    getPlayerStyle: (type: string, role: string, id: number) => PlayerStyle;
    getThemeSetting: (prop: string, player: any) => { allFrames: boolean; wholeTeam: boolean };
    getBallStyle: () => BallStyle;
    getFieldStyle: () => FieldStyle;
}

export const PlayThemeContext = createContext<PlayThemeContextValue>(null);

export const usePlayTheme = (): PlayThemeContextValue => {
    return useContext(PlayThemeContext);
};

interface Props {
    branding: any;
    teamLogoUrl?: string;
}

export const PlayThemeProvider: FC<Props> = ({ children, branding, teamLogoUrl }) => {
    const { playData, PlayConfig, currentFrame } = usePlayEditor();
    const { colorPrimary = '#000229', colorSecondary = '#ff4f00' } = branding ?? playData?.branding ?? {};

    const { PlayTheme } = PlayConfig;

    const getThemeSetting = (prop: string, player: any) => {
        const wholeTeamThisFrame = Boolean(currentFrame.theme?.find((theme) => theme.target === player.type)?.[prop]);
        const thisPlayerAllFrames = Boolean(player.props?.[prop]);
        const wholeTeamAllFrames = Boolean(playData.theme?.[player.type]?.[prop]);

        return {
            wholeTeam: wholeTeamAllFrames || wholeTeamThisFrame,
            allFrames: thisPlayerAllFrames || wholeTeamAllFrames,
        };
    };

    const getPlayerColor = (type: string, role: string, id: number) => {
        const defaultFill = type === ToolTypes.BLUEPLAYER ? colorPrimary : colorSecondary;

        const typeFill = PlayTheme?.Players?.[type]?.fill;
        const roleFill = PlayTheme?.Players?.[role]?.fill;

        const globalFill = playData.theme?.[type]?.color;
        const teamFill = currentFrame.theme?.find((theme) => theme.target === type)?.color;
        const playerFill = playData.initial?.find((player) => player.id === id)?.color;
        const frameFill = currentFrame.theme?.find((theme) => theme.target === id)?.color;

        return globalFill ?? teamFill ?? playerFill ?? frameFill ?? roleFill ?? typeFill ?? defaultFill;
    };

    const getPlayerShape = (type: string, role: string, id: number) => {
        const typeShape = PlayTheme?.Players?.[type]?.shape;
        const roleShape = PlayTheme?.Players?.[role]?.shape;

        const globalShape = playData.theme?.[type]?.shape;
        const teamShape = currentFrame.theme?.find((theme) => theme.target === type)?.shape;
        const playerShape = playData.initial?.find((player) => player.id === id)?.shape;
        const frameShape = currentFrame.theme?.find((theme) => theme.target === id)?.shape;

        return globalShape ?? teamShape ?? playerShape ?? frameShape ?? roleShape ?? typeShape ?? PlayerShapes.CIRCLE;
    };

    const getPlayerStyle = (type: string, role: string, id: number): PlayerStyle => {
        const fill = getPlayerColor(type, role, id);
        const shape = getPlayerShape(type, role, id);

        return {
            fill,
            shape,
        };
    };

    const getBallStyle = () => {
        return PlayTheme?.Balls;
    };

    const getFieldStyle = (): FieldStyle => {
        const realField = playData?.viewOptions?.realField === true;
        const useGrassFill = realField && PlayConfig.Site !== Sites.hoopslab;
        const fieldFill = useGrassFill ? REAL_FIELD_FILL_DEFAULT : 'white';
        const fieldLogoOpacity = realField ? FIELD_LOGO_OPACITY_REAL_FIELD : FIELD_LOGO_OPACITY;
        const fieldStroke = realField && PlayConfig.Site === Sites.hoopslab ? 'white' : '#ddd';

        return {
            fieldStroke,
            fieldFill,
            movementStroke: '#333',
            fieldLogoOpacity,
        };
    };

    const value = {
        colorPrimary,
        colorSecondary,
        logoUrl: teamLogoUrl,
        getPlayerStyle,
        getThemeSetting,
        getBallStyle,
        getFieldStyle,
    };

    return <PlayThemeContext.Provider value={value}>{children}</PlayThemeContext.Provider>;
};
